import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StudioSidebar from '../../components/ui/StudioSidebar';

const StudioGames = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);

  useEffect(() => {
    // Allow both studio_admin and admin roles
    if (!user || (userProfile?.role !== 'studio_admin')) {
      navigate('/');
      return;
    }

    loadGames();
  }, [user, userProfile, navigate]);

  const loadGames = async () => {
    setLoading(true);
    try {
      // This would load games from the API
      // For now, we'll use placeholder data
      setGames([]);
    } catch (error) {
      console.error('Failed to load games:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StudioSidebar />
      
      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Games</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Your Games
              </h1>
              <p className="text-muted-foreground">
                Manage and monitor your published games
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                onClick={() => navigate('/studio-game-upload')}
              >
                Upload New Game
              </Button>
            </div>
          </div>

          {games.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Gamepad2" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Games Yet</h3>
              <p className="text-muted-foreground mb-6">
                Upload your first game to get started with the Solis Games platform
              </p>
              <Button onClick={() => navigate('/studio-game-upload')}>
                Upload Your First Game
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {games.map((game) => (
                <div key={game.id} className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-semibold text-foreground mb-2">{game.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{game.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {game.play_count} plays
                    </span>
                    <Button variant="outline" size="sm">
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StudioGames;
